
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Logo } from "@/components/brand/logo";
import { usePwaInstall } from "@/hooks/use-pwa-install";
import { useTranslation } from "react-i18next";
import { Download, Share, PlusSquare, X, Smartphone } from "lucide-react";

/**
 * PwaInstallBanner — dismissible bottom card that prompts the user to install
 * the HornDeliver PWA. Two flavours:
 *
 *  1. Chromium-based browsers (Chrome / Edge / Samsung / Brave on Android):
 *     the `beforeinstallprompt` event fires → we show "Install now" which
 *     triggers the native install dialog.
 *
 *  2. iOS Safari (no beforeinstallprompt): we show an "Add to Home Screen"
 *     sheet with step-by-step instructions (Share → Add to Home Screen).
 *
 * Persistence:
 *  - Dismissal is remembered in localStorage (versioned) via the hook, so the
 *    banner doesn't nag the user every session.
 *  - Already-installed (standalone) users never see it.
 *
 * Placement: fixed above the mobile bottom nav (z-30, sits below the topbar's
 * z-40). Hidden on md+ viewports because desktop PWAs are less of a concern
 * and the banner would clutter the layout.
 */
export function PwaInstallBanner() {
  const { canInstall, isIos, promptInstall, dismiss } = usePwaInstall();
  const { t } = useTranslation();
  const [iosSheetOpen, setIosSheetOpen] = useState(false);
  const [installing, setInstalling] = useState(false);

  if (!canInstall) return null;

  const onInstallClick = async () => {
    if (isIos) {
      setIosSheetOpen(true);
      return;
    }
    setInstalling(true);
    const accepted = await promptInstall();
    setInstalling(false);
    if (accepted) {
      // The appinstalled event will flip canInstall to false and unmount us.
      return;
    }
    // If the user dismissed the native prompt, also dismiss our banner so we
    // don't immediately re-prompt on the next render.
    dismiss();
  };

  return (
    <>
      <div
        className="fixed inset-x-0 bottom-[calc(56px+env(safe-area-inset-bottom))] z-30 px-3 pb-2 md:hidden"
        role="dialog"
        aria-label={t("pwa.install")}
      >
        <div className="mx-auto flex max-w-md items-center gap-3 rounded-2xl border border-primary/30 bg-background/95 p-2.5 shadow-lg ring-1 ring-primary/10 backdrop-blur-lg">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10">
            <Logo className="h-7 w-7" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold leading-tight">
              {t("pwa.install")}
            </p>
            <p className="truncate text-[11px] text-muted-foreground leading-tight">
              {isIos ? t("pwa.iosTagline") : t("pwa.installDesc")}
            </p>
          </div>
          <Button
            size="sm"
            onClick={onInstallClick}
            disabled={installing}
            className="h-8 shrink-0 gap-1 px-3 text-xs"
          >
            <Download className="h-3.5 w-3.5" />
            {installing ? "…" : isIos ? t("pwa.howTo") : t("pwa.installNow")}
          </Button>
          <button
            onClick={dismiss}
            aria-label={t("common.close")}
            className="shrink-0 rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {/* iOS instructions sheet */}
      <Sheet open={iosSheetOpen} onOpenChange={setIosSheetOpen}>
        <SheetContent side="bottom" className="mx-auto max-w-md">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <Smartphone className="h-5 w-5 text-primary" />
              {t("pwa.iosSheetTitle")}
            </SheetTitle>
            <SheetDescription>{t("pwa.iosSheetDesc")}</SheetDescription>
          </SheetHeader>
          <ol className="mt-4 space-y-3">
            <IosStep
              num={1}
              icon={Share}
              title={t("pwa.iosStep1Title")}
              desc={t("pwa.iosStep1Desc")}
            />
            <IosStep
              num={2}
              icon={PlusSquare}
              title={t("pwa.iosStep2Title")}
              desc={t("pwa.iosStep2Desc")}
            />
            <IosStep
              num={3}
              icon={Logo}
              title={t("pwa.iosStep3Title")}
              desc={t("pwa.iosStep3Desc")}
            />
          </ol>
          <div className="mt-5 flex gap-2">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => {
                setIosSheetOpen(false);
                dismiss();
              }}
            >
              {t("common.close")}
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}

function IosStep({
  num,
  icon: Icon,
  title,
  desc,
}: {
  num: number;
  icon: React.ElementType;
  title: string;
  desc: string;
}) {
  return (
    <li className="flex items-start gap-3 rounded-xl border border-border/60 bg-muted/30 p-3">
      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">
        {num}
      </span>
      <div className="min-w-0 flex-1">
        <p className="flex items-center gap-1.5 text-sm font-semibold">
          <Icon className="h-4 w-4 text-muted-foreground" />
          {title}
        </p>
        <p className="mt-0.5 text-xs text-muted-foreground">{desc}</p>
      </div>
    </li>
  );
}
